import { FC } from 'react';
import {
  Theme,
  useTranslation,
  Tag,
  TagStyle,
  IconStyle,
  AlertMessage,
  useSession,
  NextgenImage,
  LoaderType,
} from '@nextgen-web-framework/all';
import { IconName } from '@fortawesome/fontawesome-svg-core';
import { PhoneNumberDisplay } from '@commerce-profile-webcomponents/ui';
import { getCountrySpecificMasterConfig } from '@commerce-checkout-webcomponents/sdk';
import { ICheckoutConfirmedAddressDetailsProps, ICheckoutShippingConfirmedAddressLinesProps } from '../types/propTypes';
import { ShippingTypeEnums } from '../../common/config/checkoutConstants';
import CheckoutInlineText from '../../checkout-inline-text/CheckoutInlineText';
import styles from './CheckoutConfirmedAddressDetails.module.scss';

export const CheckoutConfirmedAddressDetails: FC<ICheckoutConfirmedAddressDetailsProps> = ({
  confirmedAddress,
  shippingType,
  children,
}: ICheckoutConfirmedAddressDetailsProps) => {
  const t = useTranslation();
  const { isAnonymous } = useSession();
  const isPickupExtraFieldEnabled = getCountrySpecificMasterConfig()?.UiBopisExtraFieldEnable;

  const getPhoneNumberField = (phoneNo: string) => {
    if (shippingType == ShippingTypeEnums.DELIVERY) {
      return (
        <>
          (
          <PhoneNumberDisplay phoneNumber={phoneNo} />)
        </>
      );
    }
    return <PhoneNumberDisplay phoneNumber={phoneNo} />;
  };

  return (
    <div className={styles['checkout-shipping-confirmed-address-container']}>
      <div className={styles['checkout-shipping-details']}>
        {confirmedAddress?.shippingTagLabel && confirmedAddress?.shippingTagIcon && (
          <Tag
            text={t(confirmedAddress?.shippingTagLabel)}
            className={styles['shippingIndicator']}
            icon={{
              iconName: confirmedAddress?.shippingTagIcon as IconName,
              iconStyle: IconStyle.SOLID,
            }}
            style={TagStyle.OUTLINE}
            theme={Theme.PURPLE}
          />
        )}
      </div>
      {confirmedAddress?.addressFields?.map((address: ICheckoutShippingConfirmedAddressLinesProps, indx) => (
        <div className={styles['checkout-shipping-details']} key={address.title}>
          {!address.disableIsSavedAlert &&
            !isAnonymous() &&
            shippingType == ShippingTypeEnums.DELIVERY &&
            confirmedAddress.isSaved == false && (
              <AlertMessage
                icon="exclamation-triangle"
                theme={Theme.WARNING}
                iconStyle={IconStyle.REGULAR}
                className={styles['checkout-confirmed-address-alert-msg']}
              >
                {t('checkout.CheckoutConfirmedAddressDetails.addressNotSavedText')}
              </AlertMessage>
            )}
          {address?.title && <h4 className={styles['checkout-shipping-details-label']}>{t(address?.title)}</h4>}
          {!indx && confirmedAddress?.profileName && (
            <p className={styles['checkout-shipping-profile-name']}>
              {' '}
              {t('checkout.CheckoutShippingDetailsContainer.profileAddressText', {
                profileName: confirmedAddress.profileName,
              })}
            </p>
          )}
          <div className={styles['checkout-shipping-details-value']}>
            {address?.subTitle && (
              <h4
                className={`${styles['checkout-shipping-confirmed-address-title']} ${
                  shippingType == ShippingTypeEnums.DELIVERY && styles['no-margin']
                }`}
              >
                {address?.storeId || address?.imageUrl ? (
                  <span>
                    {address?.imageUrl && (
                      <div className={styles['store-image']}>
                        <NextgenImage
                          src={address.imageUrl}
                          alt={address?.subTitle}
                          loaderType={LoaderType.CONTENTSTACK}
                        />
                      </div>
                    )}
                    <span className={styles['checkout-shipping-details-value-store-name']}>{address?.subTitle}</span>
                    {/* {address?.storeId && <span className={styles['checkout-shipping-details-value-separator']}></span>}
                    <span>{address?.storeId}</span> */}
                  </span>
                ) : (
                  address?.subTitle
                )}
              </h4>
            )}
            {isPickupExtraFieldEnabled && address?.pickupRecipientName && <p>{address?.pickupRecipientName}</p>}
            {(address?.recipientName || address?.recipientContact) && (
              <p>
                {address?.recipientName ?? ''}
                {address?.recipientContact ? <>{getPhoneNumberField(address.recipientContact)}</> : ''}
              </p>
            )}
            {isPickupExtraFieldEnabled && address?.pickupRecipientEmail && <p>{address?.pickupRecipientEmail}</p>}
            {address?.addressValues?.map((addressLine) => (
              <>{addressLine && <CheckoutInlineText text={addressLine} />}</>
            ))}
            {address?.instructions && (
              <CheckoutInlineText
                text={address.instructions}
                customClass={styles['checkout-shipping-details-instructions']}
              />
            )}
          </div>
          {!address.disableIsSavedAlert && children && (
            <div className={styles['checkout-shipping-confirmed-address-children']}>{children}</div>
          )}
        </div>
      ))}
    </div>
  );
};

export default CheckoutConfirmedAddressDetails;
